import React, { useEffect, useState } from 'react';
import SubmitButton from '../../../components/SubmitButton';
import Label from '../../../components/Label';
import { axiosKK } from '../../../services/networkRequest';
import {
  ApplyMode,
  BillingRateDTO,
  PartnerAdditionalFeeDTO,
  PartnerPricingRequest,
  PartnerPricingResponse,
} from '../../../models/APIModels';
import errorMessage from '../../../services/errorMessage';
import { AdminNotice } from '../../../components/admin/Shared';

type Scope = 'courier' | 'warehouse';

const APPLY_MODES: ApplyMode[] = ['FLAT', 'PER_BOX', 'PER_DELIVERY', 'WEEKLY'];

const KNOWN_COURIER_RATE_TYPES: string[] = [
  'COURIER_DOORSTEP',
  'COURIER_TIMESLOT',
  'FAILED_DELIVERY_FEE',
  'MIN_CHARGE',
];
const KNOWN_WAREHOUSE_RATE_TYPES: string[] = [
  'WH_STORAGE_PER_BOX',
  'WH_INBOUND_PROCESSING',
  'WH_OUTBOUND_PICK',
  'WH_FAILED_DELIVERY_FEE',
  'WH_EXTRA_TASK',
];

function displayRateType(key: string): string {
  return key
    .replace(/^WH_/, 'WH · ')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface Props {
  partnerId: number;
}

export default function PricingTab({ partnerId }: Props) {
  const [scope, setScope] = useState<Scope>('courier');
  const [rates, setRates] = useState<BillingRateDTO[]>([]);
  const [additionalFees, setAdditionalFees] = useState<PartnerAdditionalFeeDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [netError, setNetError] = useState<string>();
  const [saveBanner, setSaveBanner] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNetError(undefined);
    setSaveBanner(undefined);

    const url = `/admin/partners/${partnerId}/${scope}-pricing`;
    axiosKK
      .get<PartnerPricingResponse>(url)
      .then(({ data }) => {
        if (cancelled) return;
        setRates(data.rates || []);
        setAdditionalFees(data.additionalFees || []);
      })
      .catch((err) => {
        if (!cancelled) errorMessage(setNetError)(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [partnerId, scope]);

  const knownKeys = scope === 'courier' ? KNOWN_COURIER_RATE_TYPES : KNOWN_WAREHOUSE_RATE_TYPES;
  const presentKeys = new Set(rates.map((r) => r.rateType));
  const missingKnown = knownKeys.filter((k) => !presentKeys.has(k));

  const updateRate = (index: number, patch: Partial<BillingRateDTO>) => {
    setRates((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addRateRow = (rateType: string) => {
    setRates((prev) => [...prev, { rateType, rateAmount: 0, warehouseId: null }]);
  };

  const removeRateRow = (index: number) => {
    setRates((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFee = (index: number, patch: Partial<PartnerAdditionalFeeDTO>) => {
    setAdditionalFees((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  const addFee = () => {
    setAdditionalFees((prev) => [
      ...prev,
      { label: '', amountPence: 0, applyMode: 'FLAT' as ApplyMode },
    ]);
  };

  const removeFee = (index: number) => {
    setAdditionalFees((prev) => prev.filter((_, i) => i !== index));
  };

  const onSave = () => {
    setSaving(true);
    setNetError(undefined);
    setSaveBanner(undefined);

    const cleanFees = additionalFees.filter((f) => f.label.trim() !== '');

    const payload: PartnerPricingRequest = {
      rates: rates.map((r) => ({
        rateType: r.rateType,
        rateAmount: Number(r.rateAmount) || 0,
        warehouseId: null,
      })),
      additionalFees: cleanFees.map((f) => ({
        label: f.label.trim(),
        amountPence: Number(f.amountPence) || 0,
        applyMode: f.applyMode,
      })),
    };

    axiosKK
      .put<PartnerPricingResponse>(`/admin/partners/${partnerId}/${scope}-pricing`, payload)
      .then(({ data }) => {
        setRates(data.rates || []);
        setAdditionalFees(data.additionalFees || []);
        setSaveBanner('Pricing saved.');
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSaving(false));
  };

  return (
    <section className="admin-partner-pricing">
      <div className="admin-tab-bar admin-tab-bar--sub">
        <button
          type="button"
          className={scope === 'courier' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setScope('courier')}
        >
          Courier
        </button>
        <button
          type="button"
          className={scope === 'warehouse' ? 'admin-tab-bar__tab admin-tab-bar__tab--active' : 'admin-tab-bar__tab'}
          onClick={() => setScope('warehouse')}
        >
          Warehouse
        </button>
      </div>

      <AdminNotice intent="error">{netError}</AdminNotice>
      <AdminNotice intent="success">{saveBanner}</AdminNotice>

      {loading ? (
        <p>Loading pricing…</p>
      ) : (
        <>
          <div className="pricing-section">
            <h4>Rates</h4>
            <p className="pricing-hint">
              Amounts are in pence. Set a rate to 0 to disable it — the invoice will drop the line.
            </p>
            {rates.length === 0 ? (
              <p>No rates configured.</p>
            ) : (
              <table className="pricing-table">
                <thead>
                  <tr>
                    <th>Rate Type</th>
                    <th>Amount (pence)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r, i) => (
                    <tr key={`${r.rateType}-${i}`}>
                      <td>{displayRateType(r.rateType)}</td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          value={r.rateAmount}
                          onChange={(e) =>
                            updateRate(i, { rateAmount: Number(e.target.value) })
                          }
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="pricing-table__remove"
                          onClick={() => removeRateRow(i)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {missingKnown.length > 0 && (
              <div className="pricing-add-known">
                <Label>Add rate type:</Label>
                <div className="pricing-add-known__buttons">
                  {missingKnown.map((k) => (
                    <button
                      key={k}
                      type="button"
                      className="pricing-add-known__button"
                      onClick={() => addRateRow(k)}
                    >
                      + {displayRateType(k)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="pricing-section">
            <h4>Additional Fees</h4>
            <p className="pricing-hint">
              Leave the list empty to clear all additional fees on save.
            </p>
            {additionalFees.length > 0 && (
              <table className="pricing-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Amount (pence)</th>
                    <th>Apply Mode</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {additionalFees.map((f, i) => (
                    <tr key={i}>
                      <td>
                        <input
                          type="text"
                          value={f.label}
                          onChange={(e) => updateFee(i, { label: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          value={f.amountPence}
                          onChange={(e) =>
                            updateFee(i, { amountPence: Number(e.target.value) })
                          }
                        />
                      </td>
                      <td>
                        <select
                          value={f.applyMode}
                          onChange={(e) =>
                            updateFee(i, { applyMode: e.target.value as ApplyMode })
                          }
                        >
                          {APPLY_MODES.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="pricing-table__remove"
                          onClick={() => removeFee(i)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button type="button" className="pricing-add-fee" onClick={addFee}>
              + Add Fee
            </button>
          </div>

          <div className="pricing-save">
            <SubmitButton onClick={onSave} isSubmitting={saving} disabled={saving}>
              Save Pricing
            </SubmitButton>
          </div>
        </>
      )}
    </section>
  );
}
